import React, { useEffect, useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import styled from 'styled-components';
import { motion } from 'framer-motion';
import { FaCheckCircle, FaArrowRight, FaExclamationTriangle } from 'react-icons/fa';

import DnaBackground from '../components/DnaBackground';
import { confirmPayment } from '../utils/paymentService';
import { trackEvent } from '../utils/analytics';

const SuccessContainer = styled.div`
  min-height: 100vh;
  display: flex;
  justify-content: center;
  align-items: center;
  padding: 2rem;
  position: relative;
`;

const SuccessCard = styled(motion.div)`
  background-color: rgba(18, 18, 18, 0.7);
  border-radius: var(--border-radius);
  padding: 3rem;
  max-width: 600px;
  width: 100%;
  text-align: center;
  backdrop-filter: blur(10px);
  border: 1px solid rgba(255, 255, 255, 0.1);
  box-shadow: 0 5px 20px rgba(0, 0, 0, 0.15);
`;

const CheckIcon = styled(FaCheckCircle)`
  font-size: 5rem;
  color: var(--primary-color);
  margin-bottom: 2rem;
`;

const ErrorIcon = styled(FaExclamationTriangle)`
  font-size: 5rem;
  color: #f44336;
  margin-bottom: 2rem;
`;

const Title = styled.h1`
  font-size: 2.5rem;
  margin-bottom: 1rem;
  background: linear-gradient(90deg, var(--primary-color), var(--secondary-color));
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  font-weight: 700;
  letter-spacing: -0.5px;
`;

const ErrorTitle = styled.h1`
  font-size: 2.5rem;
  margin-bottom: 1rem;
  color: #f44336;
  font-weight: 700;
  letter-spacing: -0.5px;
`;

const Description = styled.p`
  font-size: 1.1rem;
  color: var(--text-secondary);
  line-height: 1.6;
  margin-bottom: 2rem;
`;

const ContinueButton = styled(motion.button)`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 10px;
  background: linear-gradient(90deg, var(--primary-color), var(--secondary-color));
  color: white;
  font-weight: 600;
  font-size: 1.1rem;
  padding: 1rem 2rem;
  border-radius: var(--border-radius);
  cursor: pointer;
  margin: 0 auto;
  text-shadow: 0 1px 2px rgba(0, 0, 0, 0.2);
  
  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 4px 12px rgba(var(--primary-rgb), 0.4);
  }
`;

const LoadingText = styled.p`
  color: var(--text-secondary);
  margin-top: 1rem;
  font-style: italic;
`;

const LoadingSpinner = styled.div`
  display: inline-block;
  width: 50px;
  height: 50px;
  border: 3px solid rgba(var(--primary-rgb), 0.2);
  border-radius: 50%;
  border-top-color: var(--primary-color);
  animation: spin 1s ease-in-out infinite;
  margin-bottom: 2rem;
  
  @keyframes spin {
    to { transform: rotate(360deg); }
  }
`;

const PaymentSuccess = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [isVerifying, setIsVerifying] = useState(true);
  const [isSuccess, setIsSuccess] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');
  
  // Extract the session ID from the URL and verify payment
  useEffect(() => {
    const verifyPayment = async () => {
      try {
        // Get session_id from URL query parameters
        const urlParams = new URLSearchParams(location.search);
        const sessionId = urlParams.get('session_id');
        
        if (sessionId) {
          console.log('Verifying payment session:', sessionId);
          // Verify payment with backend
          const confirmed = await confirmPayment(sessionId);
          console.log('Payment verification result:', confirmed ? 'Success' : 'Failed');
          setIsSuccess(confirmed);
          
          if (confirmed) {
            trackEvent('purchase_completed', { sessionId });
          } else {
            setErrorMessage('Payment verification failed. Your payment may still be processing.');
          }
        } else {
          // No session ID found, can't verify
          console.error('No session ID found in URL');
          setIsSuccess(false);
          setErrorMessage('No payment session ID found. Please try again or contact support.');
        }
      } catch (error) {
        console.error('Error verifying payment:', error);
        setIsSuccess(false);
        setErrorMessage(error.message || 'An unexpected error occurred while verifying your payment.');
      } finally {
        setIsVerifying(false);
      }
    };
    
    // Short delay before verification to ensure any redirects complete
    const timer = setTimeout(() => {
      verifyPayment();
    }, 500);
    
    return () => clearTimeout(timer);
  }, [location]);
  
  // Handle navigation to results
  const handleContinue = () => {
    navigate('/results');
  };
  
  return (
    <SuccessContainer>
      <DnaBackground />
      <SuccessCard
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5 }}
      >
        {isVerifying ? (
          <>
            <LoadingSpinner />
            <Title>Verifying Your Payment</Title>
            <Description>
              We're confirming your payment details. This will only take a moment...
            </Description>
            <LoadingText>Please wait...</LoadingText>
          </>
        ) : isSuccess ? (
          <>
            <CheckIcon />
            <Title>Payment Successful!</Title>
            <Description>
              Thank you for your purchase! You now have full access to the premium content 
              and insights to help you maximize your founder potential.
            </Description>
            <ContinueButton 
              onClick={handleContinue}
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
            >
              Continue to Results <FaArrowRight />
            </ContinueButton>
          </>
        ) : (
          <>
            <ErrorIcon />
            <ErrorTitle>Verification Failed</ErrorTitle>
            <Description>
              {errorMessage || "We couldn't verify your payment. If you believe this is an error, please contact support or try again."}
            </Description>
            <ContinueButton 
              onClick={handleContinue}
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
            >
              Return to Results <FaArrowRight />
            </ContinueButton>
          </>
        )}
      </SuccessCard>
    </SuccessContainer>
  );
};

export default PaymentSuccess; 